/**
 * PLAT-10046 — shared upsert / revert dispatchers for the composition
 * quick-edit popover. Both surfaces (the clip-preview canvas overlay and
 * the transcript-chip bridge) wrap the same `TranslationStore` calls with
 * the same belt-and-suspenders `refreshSource()` nudge; this hook is the
 * single place that pattern lives.
 *
 * Why a hook (not a plain function):
 *
 *   - The dispatchers must memo-stabilize for the popover's `useCallback`
 *     dep arrays, so the surface gets stable references across rerenders.
 *   - The `compositionElementId` / `userIdentity.currentEditor` reads are
 *     surface-specific (`openMarker.marker.id` vs `activeTarget.id`); the
 *     surfaces pass plain values in and the hook handles memoization.
 *
 * Why the `refreshSource()` calls:
 *
 *   - `useClipPreviewRenderer`'s PLAT-9966 reaction already observes
 *     `translations.get(lang)` and re-projects, but the reaction runs on
 *     the MobX scheduler tick AFTER the cache swap; the explicit
 *     `refreshSource()` here guarantees the iframe repaints on the same
 *     tick the user clicks Save / Revert. Removing this would re-introduce
 *     the visible delay reported during PLAT-10046 review.
 */
import type { CompositionPhraseTranslation } from '@src/videoTranscriptionProcessor/translation/compositionTypes';
import type {
  RemoveCompositionPhrasePinDispatch,
  UpsertCompositionPhraseDispatch,
} from '@src/components/transcript/CompositionQuickEditPopover';
import type { TranslationStore } from '@src/stores-v2/TranslationStore';
import { useCallback } from 'react';

export interface CompositionPinDispatcherDeps {
  /**
   * Active composition placement / overlay id. When `null` the dispatchers
   * no-op — the surface should typically not be rendering the popover in
   * that state, but the hook tolerates it so render order doesn't matter.
   */
  compositionElementId: string | null;
  /**
   * Per-language phrase lookup. Used to inherit the existing entry's
   * `references[]` when overriding an already-translated phrase (the
   * canonical references come from bulk-translate runs). When the entry
   * is missing entirely the hook seeds a single-reference fallback so the
   * next bulk run knows about the placement.
   */
  compositionPhrases: ReadonlyMap<string, CompositionPhraseTranslation>;
  /** Active caption language (the language being pinned / reverted). */
  lang: string;
  translationStore: TranslationStore;
  /** Display name stamped onto pin metadata as `pinnedBy`. */
  editorDisplayName: string;
  /**
   * Called immediately after every cache mutation. Both surfaces pass a
   * `refreshSource()` thunk; see the file header for why.
   */
  onAfterCommit: () => void;
}

export interface CompositionPinDispatchers {
  upsert: UpsertCompositionPhraseDispatch;
  revert: RemoveCompositionPhrasePinDispatch;
}

export function useCompositionPinDispatchers(
  deps: CompositionPinDispatcherDeps,
): CompositionPinDispatchers {
  const {
    compositionElementId,
    compositionPhrases,
    lang,
    translationStore,
    editorDisplayName,
    onAfterCommit,
  } = deps;

  const upsert = useCallback<UpsertCompositionPhraseDispatch>(
    ({ sourceText, translatedValue, previousTranslatedValue }) => {
      if (!compositionElementId) return;
      const existing = compositionPhrases.get(sourceText.trim().toLowerCase());
      const references =
        existing?.references ??
        [
          {
            compositionId: '',
            compositionElementId,
            sourceElement: {
              id: compositionElementId,
              name: '',
              type: 'text',
            },
            property: 'text',
          },
        ];
      translationStore.upsertCompositionPhraseTranslation(lang, {
        sourceText,
        translatedValue,
        references,
        pinnedBy: editorDisplayName,
        ...(previousTranslatedValue !== undefined
          ? { previousTranslatedValue }
          : {}),
      });
      onAfterCommit();
    },
    [
      compositionElementId,
      compositionPhrases,
      lang,
      translationStore,
      editorDisplayName,
      onAfterCommit,
    ],
  );

  const revert = useCallback<RemoveCompositionPhrasePinDispatch>(
    ({ sourceText }) => {
      translationStore.removeCompositionPhrasePin(lang, sourceText);
      onAfterCommit();
    },
    [lang, translationStore, onAfterCommit],
  );

  return { upsert, revert };
}
